import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
    SettingsField,
    SettingsGroup,
    SettingsToggleRow,
} from '../../../../components/design-system/common/SettingsPanel';

export type GroutSizeKey = 'thin' | 'medium' | 'thick';
export type GroutSizes = Record<GroutSizeKey, { label: string; value: string }>;

/** Everything a vendor sets about grout in the visualizer, as the page holds it. */
export interface GroutSettings {
    enabled: boolean;
    colors: string[];
    /** The shop's own name for a colour, keyed by uppercase #RRGGBB. */
    colorNames: Record<string, string>;
    /** The widths offered, in mm, sorted. */
    widths: number[];
    /** Shoppers may set any width. */
    customWidth: boolean;
    /** Shoppers may pick any colour. */
    customColor: boolean;
}

/** What the API gives a vendor that never set them (pms-config entity defaults). */
export const DEFAULT_GROUT_COLORS = ['#4C4C4C', '#FFFFFF', '#D6D2CB', '#A8A29E', '#000000'];
export const DEFAULT_GROUT_WIDTHS = [2, 4, 6];
export const DEFAULT_GROUT_SETTINGS: GroutSettings = {
    enabled: false,
    colors: DEFAULT_GROUT_COLORS,
    colorNames: {},
    widths: DEFAULT_GROUT_WIDTHS,
    customWidth: true,
    customColor: false,
};

/** The API's bounds (grout-options.ts). */
const MAX_WIDTHS = 8;
const MIN_WIDTH = 0.5;
const MAX_WIDTH = 20;
const MAX_NAME = 40;

const sortedWidths = (widths: number[]) => [...new Set(widths.map((w) => Math.round(w * 10) / 10))].sort((a, b) => a - b);

/** The page's grout state from a stored config; widths fall back to the old thin/medium/thick values. */
export function groutSettingsFromConfig(config: any): GroutSettings {
    const stored = Array.isArray(config?.groutWidths) ? config.groutWidths.map(Number).filter(Number.isFinite) : [];
    const legacy = config?.groutSizes
        ? (['thin', 'medium', 'thick'] as GroutSizeKey[]).map((k) => parseFloat(config.groutSizes[k]?.value)).filter(Number.isFinite)
        : [];
    return {
        enabled: config?.groutEnabled === true,
        colors: Array.isArray(config?.groutColors) && config.groutColors.length ? config.groutColors : DEFAULT_GROUT_COLORS,
        colorNames: config?.groutColorNames && typeof config.groutColorNames === 'object' ? config.groutColorNames : {},
        widths: sortedWidths(stored.length ? stored : legacy.length ? legacy : DEFAULT_GROUT_WIDTHS),
        customWidth: config?.groutCustomWidth !== false,
        customColor: config?.groutCustomColor === true,
    };
}

/**
 * The config fields to save. `groutSizes` is still written, from the widths,
 * for visualizers built before `groutWidths`: smallest, middle and largest, each
 * labelled by its width so even those show "2 mm" rather than an English "Thin".
 */
export function groutConfigFields(grout: GroutSettings) {
    const widths = grout.widths.length ? grout.widths : DEFAULT_GROUT_WIDTHS;
    const pick = (w: number) => ({ label: `${w} mm`, value: String(w) });
    const colorNames = Object.fromEntries(
        Object.entries(grout.colorNames)
            .filter(([hex, name]) => name.trim() && grout.colors.some((c) => c.toUpperCase() === hex))
            .map(([hex, name]) => [hex, name.trim()]),
    );
    return {
        groutEnabled: grout.enabled,
        groutColors: grout.colors,
        groutColorNames: colorNames,
        groutWidths: widths,
        groutCustomWidth: grout.customWidth,
        groutCustomColor: grout.customColor,
        groutSizes: {
            thin: pick(widths[0]),
            medium: pick(widths[Math.floor(widths.length / 2)]),
            thick: pick(widths[widths.length - 1]),
        },
    };
}

/**
 * Grout in the visualizer (grout v2): whether shoppers may change the grout of
 * the products that allow it, the colours they pick from (under the shop's own
 * names), the widths offered, and whether they may go beyond those.
 */
export const GroutSettingsGroup = ({
    value,
    onChange,
}: {
    value: GroutSettings;
    onChange: (value: GroutSettings) => void;
}) => {
    const { t } = useTranslation();
    const [newColor, setNewColor] = useState('#808080');
    const [newWidth, setNewWidth] = useState('3');
    const set = (patch: Partial<GroutSettings>) => onChange({ ...value, ...patch });

    const addColor = () => {
        const color = newColor.toUpperCase();
        if (!value.colors.some((c) => c.toUpperCase() === color)) set({ colors: [...value.colors, color] });
    };

    const removeColor = (color: string) => {
        const { [color.toUpperCase()]: _removed, ...colorNames } = value.colorNames;
        set({ colors: value.colors.filter((c) => c !== color), colorNames });
    };

    const nameColor = (color: string, name: string) =>
        set({ colorNames: { ...value.colorNames, [color.toUpperCase()]: name.slice(0, MAX_NAME) } });

    const width = parseFloat(newWidth.replace(',', '.'));
    const widthOk = Number.isFinite(width) && width >= MIN_WIDTH && width <= MAX_WIDTH
        && !value.widths.includes(Math.round(width * 10) / 10) && value.widths.length < MAX_WIDTHS;
    const addWidth = () => {
        if (widthOk) set({ widths: sortedWidths([...value.widths, width]) });
    };

    return (
        <SettingsGroup
            title={t('PAGES.SETTINGS.GROUT.TITLE', 'Grout')}
            description={t('PAGES.SETTINGS.GROUT.DESC', 'Let shoppers change the grout of tiles in the visualizer.')}
        >
            <SettingsToggleRow
                id="grout-enabled"
                label={t('PAGES.SETTINGS.GROUT.ENABLE', 'Shoppers can change the grout')}
                description={value.enabled
                    ? t('PAGES.SETTINGS.GROUT.ENABLE_YES', 'On, for the products whose page allows it')
                    : t('PAGES.SETTINGS.GROUT.ENABLE_NO', 'Off: every product keeps its own grout')}
                checked={value.enabled}
                onCheckedChange={(enabled) => set({ enabled })}
            >
                <SettingsField
                    label={t('PAGES.SETTINGS.GROUT.COLORS', 'Colours')}
                    htmlFor="grout-new-color"
                    hint={t('PAGES.SETTINGS.GROUT.COLORS_NAMED_HINT')}
                >
                    <div className="flex flex-col gap-2">
                        <div className="grid gap-2 sm:grid-cols-2">
                            {value.colors.map((color) => (
                                <div key={color} className="flex items-center gap-2">
                                    <span
                                        className="size-8 shrink-0 rounded-md border border-border"
                                        style={{ backgroundColor: color }}
                                        title={color}
                                    />
                                    <Input
                                        value={value.colorNames[color.toUpperCase()] ?? ''}
                                        onChange={(e) => nameColor(color, e.target.value)}
                                        placeholder={color.toUpperCase()}
                                        maxLength={MAX_NAME}
                                        aria-label={t('PAGES.SETTINGS.GROUT.COLOR_NAME', { color })}
                                        className="h-8"
                                    />
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="size-8 shrink-0 text-muted-foreground"
                                        aria-label={t('PAGES.SETTINGS.GROUT.REMOVE_COLOR', 'Remove {{color}}', { color })}
                                        onClick={() => removeColor(color)}
                                        disabled={value.colors.length <= 1}
                                    >
                                        <X className="size-3.5" aria-hidden="true" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                        <span className="flex items-center gap-1.5">
                            <input
                                id="grout-new-color"
                                type="color"
                                value={newColor}
                                onChange={(e) => setNewColor(e.target.value)}
                                className="size-8 cursor-pointer rounded-md border border-border bg-transparent p-0.5"
                            />
                            <Button type="button" variant="outline" size="sm" onClick={addColor}>
                                <Plus className="size-3.5" aria-hidden="true" />
                                {t('PAGES.SETTINGS.GROUT.ADD_COLOR', 'Add')}
                            </Button>
                        </span>
                    </div>
                </SettingsField>

                <SettingsToggleRow
                    id="grout-custom-color"
                    label={t('PAGES.SETTINGS.GROUT.CUSTOM_COLOR')}
                    description={value.customColor
                        ? t('PAGES.SETTINGS.GROUT.CUSTOM_COLOR_YES')
                        : t('PAGES.SETTINGS.GROUT.CUSTOM_COLOR_NO')}
                    checked={value.customColor}
                    onCheckedChange={(customColor) => set({ customColor })}
                />

                <SettingsField
                    label={t('PAGES.SETTINGS.GROUT.SIZES', 'Widths (mm)')}
                    htmlFor="grout-new-width"
                    hint={t('PAGES.SETTINGS.GROUT.WIDTHS_HINT')}
                >
                    <div className="flex flex-wrap items-center gap-2">
                        {value.widths.map((w) => (
                            <span
                                key={w}
                                className="inline-flex h-8 items-center gap-1 rounded-md border border-border ps-2.5 pe-1 text-sm"
                            >
                                {t('PAGES.SETTINGS.GROUT.WIDTH_MM', { value: w })}
                                <button
                                    type="button"
                                    aria-label={t('PAGES.SETTINGS.GROUT.REMOVE_WIDTH', { value: w })}
                                    onClick={() => set({ widths: value.widths.filter((x) => x !== w) })}
                                    disabled={value.widths.length <= 1}
                                    className="flex size-5 items-center justify-center rounded text-muted-foreground hover:bg-muted disabled:opacity-40"
                                >
                                    <X className="size-3" aria-hidden="true" />
                                </button>
                            </span>
                        ))}
                        <span className="flex items-center gap-1.5">
                            <Input
                                id="grout-new-width"
                                type="number"
                                min={MIN_WIDTH}
                                max={MAX_WIDTH}
                                step={0.5}
                                value={newWidth}
                                onChange={(e) => setNewWidth(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addWidth(); } }}
                                className="h-8 w-20"
                            />
                            <Button type="button" variant="outline" size="sm" onClick={addWidth} disabled={!widthOk}>
                                <Plus className="size-3.5" aria-hidden="true" />
                                {t('PAGES.SETTINGS.GROUT.ADD_COLOR', 'Add')}
                            </Button>
                        </span>
                    </div>
                </SettingsField>

                <SettingsToggleRow
                    id="grout-custom-width"
                    label={t('PAGES.SETTINGS.GROUT.CUSTOM_WIDTH')}
                    description={value.customWidth
                        ? t('PAGES.SETTINGS.GROUT.CUSTOM_WIDTH_YES')
                        : t('PAGES.SETTINGS.GROUT.CUSTOM_WIDTH_NO')}
                    checked={value.customWidth}
                    onCheckedChange={(customWidth) => set({ customWidth })}
                />
            </SettingsToggleRow>
        </SettingsGroup>
    );
};
